import React, { createContext, Dispatch, useEffect, useMemo, useReducer } from 'react'
import { CalendarEvent } from '@chilipiper/scheduling-shared'
import { differenceBy, union, uniqBy } from 'lodash'

interface PrioritySettingsState {
  isPrioritySelectionEnabled: boolean
  retrieved: CalendarEvent[]
  selected: CalendarEvent[]
  singlePrioritySelection: CalendarEvent | undefined
  totalPriorityEventsSelection: CalendarEvent[]
  unselected: CalendarEvent[]
}

interface SetRetrievedAction {
  payload: CalendarEvent[]
  type: 'SET_RETRIEVED'
}

interface SetSelectedAction {
  payload: CalendarEvent[]
  type: 'SET_SELECTED'
}

interface SetUnselectedAction {
  payload: CalendarEvent[]
  type: 'SET_UNSELECTED'
}

interface SetTotalPriorityEventsSelection {
  payload: CalendarEvent[]
  type: 'SET_TOTAL_PRIORITY_EVENTS_SELECTION'
}

interface SetSinglePrioritySelectionAction {
  payload: CalendarEvent | undefined
  type: 'SET_SINGLE_PRIORITY_SELECTION'
}

interface SetIsPrioritySelectionEnabled {
  payload: boolean
  type: 'SET_IS_PRIORITY_SELECTION_ENABLED'
}

interface ResetAction {
  type: 'RESET'
}

type PrioritySettingsAction =
  | SetRetrievedAction
  | SetSelectedAction
  | SetUnselectedAction
  | SetTotalPriorityEventsSelection
  | SetSinglePrioritySelectionAction
  | SetIsPrioritySelectionEnabled
  | ResetAction

const initialState: PrioritySettingsState = {
  retrieved: [],
  selected: [],
  unselected: [],
  totalPriorityEventsSelection: [],
  singlePrioritySelection: undefined,
  isPrioritySelectionEnabled: false,
}

const prioritySettingsReducer = (
  state: PrioritySettingsState,
  action: PrioritySettingsAction
): PrioritySettingsState => {
  switch (action.type) {
    case 'SET_RETRIEVED':
      return { ...state, retrieved: action.payload }
    case 'SET_SELECTED':
      return { ...state, selected: action.payload }
    case 'SET_UNSELECTED':
      return { ...state, unselected: action.payload }
    case 'SET_TOTAL_PRIORITY_EVENTS_SELECTION':
      return { ...state, totalPriorityEventsSelection: action.payload }
    case 'SET_SINGLE_PRIORITY_SELECTION':
      return { ...state, singlePrioritySelection: action.payload }
    case 'SET_IS_PRIORITY_SELECTION_ENABLED':
      return { ...state, isPrioritySelectionEnabled: action.payload }
    case 'RESET':
      return { ...initialState }
  }
}

interface PrioritySettingsContextType {
  dispatch: Dispatch<PrioritySettingsAction>
  state: PrioritySettingsState
}

export const PrioritySettingsContext = createContext<PrioritySettingsContextType>(
  {} as PrioritySettingsContextType
)

export const PrioritySettingsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(prioritySettingsReducer, initialState)

  useEffect(() => {
    const retrievedWithoutUnselected = differenceBy(state.retrieved, state.unselected, 'id')
    const combinedEvents = uniqBy(union(retrievedWithoutUnselected, state.selected), 'id')
    dispatch({ type: 'SET_TOTAL_PRIORITY_EVENTS_SELECTION', payload: combinedEvents })
  }, [state.retrieved, state.selected, state.unselected])

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch])

  return (
    <PrioritySettingsContext value={value}>{children}</PrioritySettingsContext>
  )
}
